/**
 * Clone to lines (W2-11 batch-tools; DESIGN.md §5.8, RPE event cloning CLONE-01..06, E§4.10).
 *
 * Source: selected segments (and optionally notes) of ONE line. Each run of consecutive selected segments of a
 * track is copied as a slice (same property, same layer). Targets are line ids (picked, or created: N new lines
 * named from a pattern, optionally with the source's parent, draw order, group and type), so reordering lines later
 * cannot change the result. For target k (k = 0..K-1, in the given order):
 *  - times shift by k * stagger;
 *  - X, Y, rotation and opacity slices get the ramp value Z_k added to every value (the Distribute engine,
 *    ramp.ts, over the K targets; each property has its own seeded jitter); opacity stays integral;
 *  - motion already present in a target's span is replaced (the curve outside the span stays exactly as it was),
 *    or the slice is skipped there, or the slice goes into a new additive layer (summed properties; others are
 *    replaced, which is noted);
 *  - notes are copied (shifted) unless the target is a UI mover (Phira would not draw them).
 * Without "keep source" (and when the source line is not itself a target) the source motion is removed: each
 * copied run becomes a Stay at its start value (Phira holds that value until the next key, as when RPE events are
 * deleted), and copied notes are deleted.
 * X/Y-linked lines keep both position axes keyed at the same beats.
 */

import { HOLD } from '../ease/index';
import { Expr } from '../expr/index';
import { mixSeed } from '../math/rng';
import { Defaults } from '../model/defaults';
import { kindOfLine } from '../model/lineKind';
import { PROP_SPECS } from '../model/props';
import type { Key, KeyId, Line, LineId, LineProp, Note, NoteId, TrackId, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { MessageRef } from '../util/types';
import {
  cleanLinks,
  collectSplit,
  commitKeys,
  expandNamePattern,
  freeLayer,
  lineCommands,
  MAX_BATCH_LINES,
  noteCommands,
  noteWorstSplit,
  readKeys,
  requireTrack,
  tidy,
  type KeyRec,
  type SliceKey,
  type SplitOn,
} from '../commands/kit';
import type { NoteDraft } from '../commands/types';
import { EarliestBeat, noteTimeOrder, spanHasMotion, toolReason, TOOLS_MSG } from './common';
import { rampValues } from './ramp';
import { alignXy, writeSlice } from './write';
import type { CloneParams, RampSpec, ToolReport } from './types';

export type RampProp = 'moveX' | 'moveY' | 'rotate' | 'alpha';
export const RAMP_PROPS: readonly RampProp[] = ['moveX', 'moveY', 'rotate', 'alpha'];

/** A run of consecutive selected segments of one source track. */
export interface CloneSlice {
  readonly prop: LineProp;
  readonly layer: number;
  readonly keys: readonly SliceKey[];
}

/** True when a ramp changes nothing. */
export function isNeutralRamp(r: RampSpec): boolean {
  return r.from === 0 && r.to === 0 && r.jitter === 0;
}

/** Ramp values per property over `count` targets (each property with its own seeded jitter). */
export function cloneRampValues(
  ramps: CloneParams['ramps'],
  count: number,
  seed: number,
): Partial<Record<RampProp, number[]>> {
  const out: Partial<Record<RampProp, number[]>> = {};
  RAMP_PROPS.forEach((prop, i) => {
    const r = ramps[prop];
    if (r !== undefined && !isNeutralRamp(r)) out[prop] = rampValues(r, count, Expr.rng(mixSeed(seed, i)));
  });
  return out;
}

/** Slices of the selected segments of one track (runs of consecutive segments). */
function slicesOfTrack(tx: TxBuilder, track: TrackId, selected: ReadonlySet<KeyId>): CloneSlice[] {
  const t = tx.doc.tracks.get(track);
  if (t?.owner.kind !== 'line') return [];
  const keys = readKeys(tx, track);
  const out: CloneSlice[] = [];
  let i = 0;
  while (i < keys.length - 1) {
    if (!selected.has((keys[i] as KeyRec).id as KeyId)) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < keys.length - 1 && selected.has((keys[j + 1] as KeyRec).id as KeyId)) j++;
    const run = keys.slice(i, j + 2);
    out.push({
      prop: t.owner.prop,
      layer: t.owner.layer,
      keys: run.map((k, n) => ({
        t: k.t,
        vIn: n === 0 ? k.vOut : k.vIn,
        vOut: n === run.length - 1 ? k.vIn : k.vOut,
        ease: k.ease,
        link: 0,
      })),
    });
    i = j + 1;
  }
  return out;
}

/** The source: one line, its slices and notes. */
function collectSource(
  tx: TxBuilder,
  p: CloneParams,
): { line: Line; slices: CloneSlice[]; notes: Note[]; runs: Map<TrackId, Set<KeyId>> } {
  const lines = new Set<LineId>();
  const runs = new Map<TrackId, Set<KeyId>>();
  for (const id of p.sourceSegments) {
    const k: Key | undefined = tx.doc.keys.get(id);
    if (k === undefined || tx.idx.nextKey(id) === undefined) continue;
    const owner = tx.doc.tracks.get(k.track)?.owner;
    if (owner?.kind !== 'line') continue;
    lines.add(owner.line);
    const set = runs.get(k.track);
    if (set === undefined) runs.set(k.track, new Set([id]));
    else set.add(id);
  }
  const notes: Note[] = [];
  if (p.includeNotes) {
    const seen = new Set<NoteId>();
    for (const id of p.sourceNotes) {
      const n = tx.doc.notes.get(id);
      if (n === undefined || seen.has(id)) continue;
      seen.add(id);
      notes.push(n);
      lines.add(n.line);
    }
    notes.sort(noteTimeOrder);
  }
  if (lines.size === 0) tx.fail(toolReason(TOOLS_MSG.cloneNothing));
  if (lines.size > 1) tx.fail(toolReason(TOOLS_MSG.cloneOneLine));
  const lineId = [...lines][0] as LineId;
  const line = tx.doc.lines.get(lineId) ?? tx.fail(toolReason(TOOLS_MSG.lineMissing));
  const slices: CloneSlice[] = [];
  for (const [track, set] of runs) slices.push(...slicesOfTrack(tx, track, set));
  return { line, slices, notes, runs };
}

type AddLineArgs = Parameters<typeof lineCommands.addLine>[1];

/** The `addLine` arguments that make a new line of the source's type (or a standard line). */
function presetFor(tx: TxBuilder, src: Line, copyType: boolean): AddLineArgs {
  const standard: AddLineArgs = { preset: 'standard' };
  if (!copyType) return standard;
  if (src.attachUI !== null) return { preset: 'uiMover', attachUI: src.attachUI };
  switch (kindOfLine(tx.doc, tx.idx, src)) {
    case 'texture':
      return src.texture === null ? standard : { preset: 'image', texture: src.texture };
    case 'gif':
      return src.texture === null ? standard : { preset: 'gif', texture: src.texture };
    case 'text': {
      const track = tx.idx.track(src.id, 'text', 0);
      const first = track === undefined ? undefined : tx.idx.keysByTrack(track)[0];
      const v = first === undefined ? undefined : tx.doc.keys.get(first)?.vOut;
      return typeof v === 'string' ? { preset: 'text', text: v } : { preset: 'text' };
    }
    case 'paint':
      return { preset: 'paint' };
    case 'normal':
      return standard;
  }
}

function createTargets(tx: TxBuilder, src: Line, spec: Extract<CloneParams['targets'], { kind: 'create' }>): LineId[] {
  if (!Number.isSafeInteger(spec.count) || spec.count < 1 || spec.count > MAX_BATCH_LINES) {
    tx.fail(toolReason(TOOLS_MSG.cloneCount, { max: MAX_BATCH_LINES }));
  }
  const extra = presetFor(tx, src, spec.copyType);
  const pattern = spec.namePattern.trim();
  const ids: LineId[] = [];
  let after: LineId = src.id;
  for (let k = 0; k < spec.count; k++) {
    const name = pattern === '' ? undefined : expandNamePattern(pattern, k, spec.count);
    const id = lineCommands.addLine(tx, {
      ...extra,
      ...(name === undefined ? {} : { name }),
      ...(spec.copyParent ? { parent: src.parent } : {}),
      after,
    });
    const patch: { zOrder?: number; group?: Line['group']; rotateWithParent?: boolean } = {};
    if (spec.copyZ) patch.zOrder = src.zOrder;
    if (spec.copyGroup) patch.group = src.group;
    if (spec.copyParent && src.parent !== null) patch.rotateWithParent = src.rotateWithParent;
    if (Object.keys(patch).length > 0) lineCommands.patchLine(tx, { id, patch });
    ids.push(id);
    after = id;
  }
  return ids;
}

/** Shifted (and ramped) copy of a slice. */
function shiftedSlice(tx: TxBuilder, s: CloneSlice, dt: Beat, add: number): SliceKey[] {
  const ramp = (v: TrackValue): TrackValue => {
    if (typeof v !== 'number' || add === 0) return v;
    const r = tidy(v + add);
    return s.prop === 'alpha' ? Math.round(r) : r;
  };
  return s.keys.map((k) => {
    const t = Beats.add(k.t, dt, tx.rounding);
    if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
    return { t, vIn: ramp(k.vIn), vOut: ramp(k.vOut), ease: k.ease, link: 0 };
  });
}

/** Turns each copied run of the source into a Stay at its start value (the source motion is removed). */
function removeSourceRuns(tx: TxBuilder, runs: ReadonlyMap<TrackId, ReadonlySet<KeyId>>, unlinked: Set<number>): void {
  for (const [trackId, selected] of runs) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const kept: KeyRec[] = [];
    let i = 0;
    while (i < keys.length) {
      const k = keys[i] as KeyRec;
      kept.push(k);
      if (k.id !== null && selected.has(k.id) && i + 1 < keys.length) {
        k.ease = HOLD;
        if (k.link !== 0) unlinked.add(k.link);
        k.link = 0;
        // Interior keys of the run go; the run's end key stays (its arrive value becomes the stayed value).
        let j = i + 1;
        while (
          j + 1 < keys.length &&
          (keys[j] as KeyRec).id !== null &&
          selected.has((keys[j] as KeyRec).id as KeyId)
        ) {
          const link = (keys[j] as KeyRec).link;
          if (link !== 0) unlinked.add(link);
          j++;
        }
        i = j;
        continue;
      }
      i++;
    }
    commitKeys(tx, info, kept);
  }
}

export function cloneToLines(tx: TxBuilder, p: CloneParams): ToolReport {
  const src = collectSource(tx, p);
  const notesOut: MessageRef[] = [];
  let targets: LineId[];
  if (p.targets.kind === 'lines') {
    const seen = new Set<LineId>();
    targets = [];
    let missing = 0;
    for (const id of p.targets.lines) {
      if (seen.has(id)) continue;
      seen.add(id);
      if (tx.doc.lines.has(id)) targets.push(id);
      else missing++;
    }
    if (missing > 0) notesOut.push(toolReason(TOOLS_MSG.cloneTargetSkipped, { count: missing }));
    if (targets.length === 0) tx.fail(toolReason(TOOLS_MSG.cloneNoTargets));
  } else {
    targets = createTargets(tx, src.line, p.targets);
  }

  const K = targets.length;
  const ramps = cloneRampValues(p.ramps, K, p.seed);
  for (const prop of RAMP_PROPS) {
    if (ramps[prop] !== undefined && !src.slices.some((s) => s.prop === prop)) {
      notesOut.push(toolReason(TOOLS_MSG.cloneNotApplicable, { prop }));
    }
  }

  const unlinked = new Set<number>();
  let worst: SplitOn | null = null;
  const segments: KeyId[] = [];
  const newNotes: NoteId[] = [];
  let skipped = 0;
  let fallback = 0;
  let uiMoverTargets = 0;
  let writes = 0;
  const focus = new EarliestBeat();

  targets.forEach((target, k) => {
    const dt = Beats.mulInt(p.stagger, k, tx.rounding);
    const sameAsSource = target === src.line.id && Beats.eq(dt, Beats.ZERO);
    const newLayerOf = new Map<string, number>();
    const takenLayers = new Map<LineProp, Set<number>>();
    const xySpans = new Map<number, [Beat, Beat][]>();
    for (const s of src.slices) {
      const add = ramps[s.prop as RampProp]?.[k] ?? 0;
      if (sameAsSource && add === 0) continue;
      const slice = shiftedSlice(tx, s, dt, add);
      const first = (slice[0] as SliceKey).t;
      const last = (slice[slice.length - 1] as SliceKey).t;
      let layer = s.layer;
      const existing = tx.idx.track(target, s.prop, s.layer);
      const busy = existing !== undefined && spanHasMotion(readKeys(tx, existing), first, last);
      if (busy && p.conflict === 'skip') {
        skipped++;
        continue;
      }
      if (busy && p.conflict === 'newLayer') {
        const key = `${s.prop}:${String(s.layer)}`;
        const known = newLayerOf.get(key);
        if (known !== undefined) layer = known;
        else if (PROP_SPECS[s.prop].layered) {
          const taken = takenLayers.get(s.prop) ?? new Set<number>();
          const free = freeLayer(tx, target, s.prop, taken);
          if (free === null) fallback++;
          else {
            layer = free;
            newLayerOf.set(key, free);
            taken.add(free);
            takenLayers.set(s.prop, taken);
          }
        } else fallback++;
      }
      const w = writeSlice(tx, target, s.prop, layer, slice, unlinked);
      worst = collectSplit(worst, w.info, w.report);
      segments.push(...w.ids.slice(0, -1));
      writes++;
      focus.add(first);
      if (s.prop === 'moveX' || s.prop === 'moveY') {
        const spans = xySpans.get(layer) ?? [];
        spans.push([first, last]);
        xySpans.set(layer, spans);
      }
    }
    for (const [layer, spans] of xySpans) alignXy(tx, target, layer, spans, ['moveX', 'moveY'], unlinked);

    if (src.notes.length > 0 && !sameAsSource) {
      const line = tx.doc.lines.get(target);
      if (line?.attachUI !== null) {
        uiMoverTargets++;
      } else {
        const drafts: NoteDraft[] = src.notes.map((n) => {
          const t = Beats.add(n.t, dt, tx.rounding);
          if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
          return {
            line: target,
            kind: n.kind,
            t,
            ...(n.kind === 'hold' ? { end: Beats.add(n.end, dt, tx.rounding) } : {}),
            x: n.x,
            overrides: {
              above: n.above,
              fake: n.fake,
              speed: n.speed,
              yOffset: n.yOffset,
              size: n.size,
              alpha: n.alpha,
              visibleTime: n.visibleTime,
              hitsound: n.hitsound,
              tint: n.tint,
              tintHitEffects: n.tintHitEffects,
              judgeArea: n.judgeArea,
            },
          };
        });
        const ids = noteCommands.addNotes(tx, {
          drafts,
          template: Defaults.noteTemplate(),
        });
        newNotes.push(...ids);
        writes++;
        const t0 = drafts[0]?.t;
        if (t0 !== undefined) focus.add(t0);
      }
    }
  });

  if (!p.keepSource && !targets.includes(src.line.id)) {
    removeSourceRuns(tx, src.runs, unlinked);
    if (src.notes.length > 0) {
      noteCommands.deleteNotes(tx, { ids: src.notes.map((n) => n.id) });
    }
    const spans: [Beat, Beat][] = src.slices
      .filter((s) => s.prop === 'moveX' || s.prop === 'moveY')
      .map((s) => [(s.keys[0] as SliceKey).t, (s.keys[s.keys.length - 1] as SliceKey).t]);
    for (const layer of new Set(src.slices.map((s) => s.layer))) {
      alignXy(tx, src.line.id, layer, spans, ['moveX', 'moveY'], unlinked);
    }
    notesOut.push(toolReason(TOOLS_MSG.cloneSourceRemoved));
  }

  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, worst);
  if (skipped > 0) notesOut.push(toolReason(TOOLS_MSG.cloneSkippedOverlap, { count: skipped }));
  if (fallback > 0) notesOut.push(toolReason(TOOLS_MSG.cloneNewLayerFallback, { count: fallback }));
  if (uiMoverTargets > 0) notesOut.push(toolReason(TOOLS_MSG.cloneNotesOnUiMover, { count: uiMoverTargets }));
  for (const n of notesOut) tx.note(n);
  tx.setSelection({
    segments,
    keys: [],
    notes: newNotes,
    ...(p.targets.kind === 'create' ? { lines: targets } : {}),
  });
  const firstTarget = targets[0];
  if (focus.beat !== null)
    tx.setFocus(firstTarget === undefined ? { beat: focus.beat } : { beat: focus.beat, line: firstTarget });
  return notesOut.length === 0 ? { changed: writes } : { changed: writes, notes: notesOut };
}
